<div id="int-events-main" class="w-full h-full flex flex-col">
  <app-internal-toolbar [title]="'internal.events.title' | translate">
    @if (api.hasPermission('create_events')) {
      <reactive-button
        actions
        [label]="'internal.events.newEvent' | translate"
        icon="add"
        variant="outlined"
        (onClick)="editEvent()"
      ></reactive-button>
    }
  </app-internal-toolbar>

  <div class="flex flex-row w-full flex-1 min-h-0">
    <!-- min-w-96-->
    <div id="int-events-list" class="flex-1 flex flex-col" [class.hide]="selectedEvent() != undefined">
      <div id="event-filter" class="m-4 flex flex-col gap-2">
        <p-select class="w-full" [options]="allSchoolyears()" [(ngModel)]="selectedSchoolyear" [loading]="loading()">
          <ng-template #selectedItem let-year>
            <div>{{ year }} - {{ year + 1 }}</div>
          </ng-template>
          <ng-template let-year #item>
            <div>{{ year }} - {{ year + 1 }}</div>
          </ng-template>
        </p-select>

        <p-iconfield class="w-full">
          <p-inputicon>
            <mat-icon>search</mat-icon>
          </p-inputicon>
          <input
            class="w-full"
            #searchInput
            type="text"
            pInputText
            [placeholder]="'general.searchPlaceholder' | translate"
            (keyup)="searchFilter.set($event.target.value)"
            (keydown.enter)="searchInput.blur()"
          />
          @if (searchInput.value) {
            <p-inputicon
              class="cursor-pointer"
              (click)="searchInput.value = ''; searchFilter.set('')"
            >
              <mat-icon>close</mat-icon>
            </p-inputicon>
          }
        </p-iconfield>
      </div>

      <div id="int-events-list-scroll" class="flex-1 min-h-0 overflow-y-auto pb-4">
        <div id="maintitle" class="text-xl font-bold text-center mb-8">
          {{ 'events.nextEvents' | translate }}
        </div>

        <div class="event-list">
          @for (event of filteredEvents(); track event) {
            <event-item
              [event]="event"
              [location]="event.location"
              (eventClicked)="selectEvent(event)"
              [loading]="loading()"
            ></event-item>
          } @empty {
            <div class="text-xl text-neutral-500 text-center">
              {{ 'events.currentNoEvents' | translate }}
            </div>
          }
        </div>

        @if (oldEvents().length > 0) {
          <div id="maintitle" class="text-xl font-bold text-center mb-8 mt-8">
            {{ 'internal.events.oldEvents' | translate }}
          </div>

          <div class="event-list">
            @for (event of filteredOldEvents(); track event) {
              <event-item
                [event]="event"
                [location]="event.location"
                (eventClicked)="selectEvent(event)"
                [loading]="loading()"
              ></event-item>
            }
          </div>
        }
      </div>
    </div>

    <div class="v-spacer desktop"></div>

    <div
      id="event-details-maininfo"
      class="flex-4 min-w-0 overflow-y-auto pl-4 pr-4 pb-4 flex flex-col"
      [class.hide]="selectedEvent() == undefined"
    >
      @if (selectedEvent()) {
        <div class="text-3xl flex flex-row items-center mb-1">
          <p-button
            [rounded]="true"
            [text]="true"
            (onClick)="selectEvent(undefined)"
            class="mobile"
          >
            <mat-icon color="white">arrow_back</mat-icon>
          </p-button>

          <span class="mr-auto">{{ selectedEvent().name }}</span>

          @if (api.hasPermission('edit_events')) {
            <p-button [rounded]="true" [text]="true" (onClick)="editEvent(selectedEvent())">
              <mat-icon color="white">edit</mat-icon>
            </p-button>
          }

          @if (api.hasPermission('edit_events') && api.hasPermission('create_finances')) {
            <p-button [rounded]="true" [text]="true" (onClick)="startInvoice()">
              <mat-icon color="white">request_quote</mat-icon>
            </p-button>
          }
        </div>

        <div class="info">
          <mat-icon>schedule</mat-icon>
          {{ selectedEvent().startdate | date: 'dd MMM yyyy HH:mm' }} -
          {{ selectedEvent().enddate | date: 'dd MMM yyyy HH:mm' }} •
          {{ eventTimespan(selectedEvent().startdate, selectedEvent().enddate) | timespan }}
        </div>

        @if (selectedEvent().location) {
          <div class="info">
            <mat-icon>location_on</mat-icon>
            {{ selectedEvent().location }}
          </div>
        }

        @if (selectedEvent().contact || selectedEvent().contactMail) {
          <div class="info">
            <mat-icon>contact_mail</mat-icon>
            {{ selectedEvent().contact }}
            @if (selectedEvent().contact && selectedEvent().contactMail) {
              <span> • </span>
            }
            @if (selectedEvent().contactMail) {
              <a
                class="bg-neutral-800 p-0.5 rounded-sm contactmail cursor-pointer"
                [href]="getMailToLink(selectedEvent())"
              >
                {{ selectedEvent().contactMail }}
            </a>
            }
          </div>
        }

        <div class="info">
          <mat-icon>public</mat-icon>
          {{
            (selectedEvent().isPublic ? 'internal.events.public' : 'internal.events.private')
              | translate
          }}
        </div>

        @if (selectedEvent().description != '') {
          <div class="h-spacer"></div>
          <div class="subtitle font-bold">
            <span class="mr-auto">{{ 'internal.events.description' | translate }}</span>
          </div>
          <div class="whitespace-pre-line">{{ selectedEvent().description }}</div>
        }

        @if (selectedEvent().equipment != '') {
          <div class="h-spacer"></div>
          <div class="subtitle font-bold">
            <span class="mr-auto">{{ 'internal.events.equipment' | translate }}</span>
          </div>
          <div class="whitespace-pre-line">{{ selectedEvent().equipment }}</div>
        }

        <div class="h-spacer"></div>
        <div class="subtitle font-bold">
          <span class="mr-auto">{{ 'internal.events.documents' | translate }}</span>
        </div>
        <document-grid
          [documents]="selectedEvent().files"
          [urlTemplate]="getDocumentUrl(selectedEvent()._id)"
          [showDownload]="true"
          [showUpload]="api.hasPermission('edit_events')"
          [showView]="true"
          [showDelete]="api.hasPermission('edit_events')"
          (uploadClicked)="uploadAttachment($event)"
          (deleteClicked)="deleteAttachment($event)"
        >
        </document-grid>

        <!-- Finances of Event -->
        @if (api.hasPermission('view_finances')) {
          <div class="h-spacer"></div>
          <div class="subtitle font-bold">
            <span class="mr-auto">{{ 'internal.events.finances' | translate }}</span>

            @if (api.hasPermission('create_finances')) {
              <p-button [rounded]="true" [text]="true" (onClick)="newFinanceTransaction()">
                <mat-icon color="white">add</mat-icon>
              </p-button>
            }
          </div>

          @if (loadingFinances()) {
            <p-skeleton height="6rem" />
          } @else {
            @if (eventFinances().length == 0) {
              <div class="text-xl font-bold text-neutral-600 text-center">
                {{ 'internal.events.noFinances' | translate }}
              </div>
            }

            @if (eventFinances().length > 0) {
              <div
                class="finance-transaction-dummy-item bg-neutral-950 rounded-sm p-2 mt-2 text-3xl"
              >
                <div class="tli-header mt-auto">
                  {{ 'internal.events.financeTotal' | translate }}
                </div>

                @if (anyFinancePending()) {
                  <span class="tli-pending large text-center mt-auto mb-auto">{{
                    'internal.finances.transaction.pending' | translate
                  }}</span>
                }

                @if (anyFinancePending()) {
                  <span class="tli-pending small text-center mt-auto mb-auto">
                    <mat-icon>money_off</mat-icon>
                  </span>
                }

                <span
                  class="tli-amount text-end mt-auto mb-auto"
                  [class.negative]="financeTotal() < 0"
                >
                  {{ financeTotal() >= 0 ? '' : '-' }}
                  {{ financeTotal().toFixed(2) | mask: 'separator' : { thousandSeparator: ',' } }}€
                </span>
              </div>
            }

            @for (trans of eventFinances(); track trans) {
              <finance-transaction-list-item
                [transaction]="trans"
                [showEditButton]="api.hasPermission('edit_finances')"
                [showDeleteButton]="api.hasPermission('delete_finances')"
                [showEvent]="false"
                (edit)="editFinanceTransaction(trans)"
                (delete)="deleteFinanceTransaction(trans)"
                class="block mt-2"
              >
              </finance-transaction-list-item>
            }
          }
        }

        <!-- Inventory of Event -->
        @if (api.hasPermission('view_inventory')) {
          <div class="h-spacer"></div>
          <div class="subtitle font-bold">
            <span class="mr-auto">{{ 'internal.events.inventory' | translate }}</span>

            @if (api.hasPermission('inventory_booking')) {
              <p-button [rounded]="true" [text]="true" (onClick)="openBookingImportDialog()">
                <mat-icon color="white">attach_file</mat-icon>
              </p-button>
            }

            @if (api.hasPermission('inventory_booking')) {
              <p-button [rounded]="true" [text]="true" (onClick)="openCreateTransactionDialog()">
                <mat-icon color="white">add</mat-icon>
              </p-button>
            }
          </div>

          @if (loadingInventory()) {
            <p-skeleton height="6rem" />
          } @else {
            <div class="flex flex-col gap-2">
              @for (trans of eventInventory(); track trans) {
                <inv-transaction-list-item
                  [transaction]="trans"
                  [showUnlinkButton]="true"
                  (changed)="selectEvent(selectedEvent())"
                  (view)="showTransaction(trans._id)"
                  (unlink)="unlinkTransaction(trans)"
                ></inv-transaction-list-item>
              } @empty {
                <div class="text-xl font-bold text-neutral-600 text-center">
                  {{ 'internal.events.noInventory' | translate }}
                </div>
              }
            </div>
          }
        }

        <!-- Images of Event -->
        @if (eventCategory() && eventImages().length > 0) {
          <div class="h-spacer"></div>
          <div class="subtitle font-bold">
            <span class="mr-auto">{{ 'internal.events.images' | translate }}</span>
          </div>

        <h2 class="text-2xl font-bold">{{eventCategory().title}}</h2>

        <p class="whitespace-pre-wrap mt-4 border-l-2 border-neutral-600 pl-2">{{eventCategory().description}}</p>

        <masonry class="mt-8 w-full" [images]="eventImages()" [loading]="loadingImages()" [useAuthImagePipe]="true" [columnWidth]="200" (imageClicked)="openImageDetails($event)">

        </masonry>
        }
      } @else {
        <div class="w-full text-center text-2xl text-neutral-500 font-bold mt-20">
          {{ 'internal.events.noEventSelected' | translate }}
        </div>
      }
    </div>
  </div>

  <!-- <app-internal-requests-edit-dialog #editDialog></app-internal-requests-edit-dialog> -->
</div>
